
import { useState, useEffect } from "react";
import { Company } from "@/components/CompanyCard";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useCompanies = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, assignee, street, postal_code, city, country, logo_url, vat_number, website_url, is_dummy_data');
      
      if (error) {
        throw error;
      }

      if (data) {
        // Transform data to match the Company interface
        const transformedData: Company[] = data.map(item => ({
          id: item.id,
          name: item.name,
          street: item.street,
          postal_code: item.postal_code,
          city: item.city,
          country: item.country,
          assignee: item.assignee || "",
          logo_url: item.logo_url,
          vat_number: item.vat_number,
          website_url: item.website_url,
          is_dummy_data: item.is_dummy_data
        }));
        
        setCompanies(transformedData);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast({
        title: "Failed to load companies",
        description: "Using sample data instead.",
        variant: "destructive",
      });
      // Fallback to mock data
      setCompanies([
        {
          id: "1",
          name: "Barco",
          street: "Boulevard du Souverain 852",
          postal_code: "1160",
          city: "Auderghem",
          country: "Belgium",
          assignee: "-",
          vat_number: "BE0417122386",
          jobCount: 3
        },
        {
          id: "2",
          name: "Belfius",
          street: "Place Roger 1",
          postal_code: "1000",
          city: "Bruxelles",
          country: "Belgium",
          assignee: "Kevin Thibaut",
          jobCount: 1
        },
        {
          id: "3",
          name: "Besix",
          street: "Avenue des Arts 20",
          postal_code: "1000",
          city: "Bruxelles",
          country: "Belgium",
          assignee: "-",
          jobCount: 2
        },
        {
          id: "4",
          name: "Logic Solutions",
          street: "Rue de la Science 14",
          postal_code: "1040",
          city: "Bruxelles",
          country: "Belgium",
          assignee: "Kevin Thibaut",
          jobCount: 5
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const logCompanyHistory = async (companyId: string, action: string, description: string) => {
    try {
      // Get the current user for the performed_by field
      let userName = "Current User";
      const { data: userSession } = await supabase.auth.getSession();
      if (userSession.session?.user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userSession.session.user.id)
          .single();
        
        if (userProfile) {
          userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || "Current User";
        }
      }
      
      // NOTE: History tracking is now handled automatically by database triggers
      // No manual history logging needed here
        
    } catch (error) {
      console.error("Error logging company history:", error);
      // We don't want to block the main operation if logging fails
    }
  };

  const addCompany = async (
    newCompany: { 
      name: string; 
      assignee?: string; 
      street?: string;
      postal_code?: string;
      city?: string;
      country?: string;
      vat_number?: string;
      website_url?: string;
      logo_url?: string;
    }
  ) => {
    try {
      // Get the user's organization UUID
      const { data: userOrgData } = await supabase.auth.getSession();
      const userUuid = userOrgData.session?.user?.id;
      
      let organizationUuid = null;
      
      if (userUuid) {
        const { data: userOrg } = await supabase
          .from('user_organization')
          .select('organization_uuid')
          .eq('user_uuid', userUuid)
          .single();
          
        organizationUuid = userOrg?.organization_uuid;
      }
      
      // Insert the new company into Supabase
      const { data, error } = await supabase
        .from('companies')
        .insert([
          { 
            name: newCompany.name,
            street: newCompany.street,
            postal_code: newCompany.postal_code,
            city: newCompany.city,
            country: newCompany.country || "Belgium",
            assignee: newCompany.assignee,
            vat_number: newCompany.vat_number,
            website_url: newCompany.website_url,
            logo_url: newCompany.logo_url,
            is_dummy_data: false,
            organization_uuid: organizationUuid
          }
        ])
        .select();
      
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        // Add the new company to the state
        const newCompanyData: Company = {
          id: data[0].id,
          name: data[0].name,
          street: data[0].street,
          postal_code: data[0].postal_code,
          city: data[0].city,
          country: data[0].country,
          assignee: data[0].assignee || "",
          vat_number: data[0].vat_number,
          website_url: data[0].website_url,
          logo_url: data[0].logo_url,
          is_dummy_data: data[0].is_dummy_data
        };
        
        setCompanies([...companies, newCompanyData]);
        
        // Log this action in company history
        await logCompanyHistory(
          data[0].id, 
          "Created", 
          `Company "${newCompanyData.name}" was created`
        );
        
        toast({
          title: "Company added",
          description: `${newCompanyData.name} has been added successfully.`,
        });

        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Error adding company:", error);
      toast({
        title: "Failed to add company",
        description: "There was an error adding the company. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  return {
    companies,
    isLoading,
    fetchCompanies,
    addCompany,
    logCompanyHistory
  };
};
